/**
 * Global session-expiry signal.
 *
 * A 401 from ANY API call (startup OR mid-gameplay: purchase / reveal / sell)
 * funnels through {@link triggerSessionExpired}, which drives the app to the
 * ExpiredScreen and emits the `session_expired` postMessage exactly once per
 * app lifetime. The one-shot dedupe means repeated 401s — or a 401 that races
 * the startup expiry path in App.tsx — fire the handler only once.
 */

let handler: (() => void) | null = null;
let fired = false;

/**
 * Register the global session-expiry handler. Returns an unsubscribe.
 * There is a single slot — the latest registration wins.
 */
export function onSessionExpired(cb: () => void): () => void {
  handler = cb;
  return () => {
    if (handler === cb) handler = null;
  };
}

/**
 * Signal that the platform session has expired (e.g. a 401 response).
 * Invokes the registered handler at most once for the lifetime of the app.
 */
export function triggerSessionExpired(): void {
  if (fired) return;
  fired = true;
  handler?.();
}

/**
 * Re-arm the one-shot latch. Embed mode only: after a 401 drops the player
 * back to anonymous browsing they can sign in again, and a LATER expiry of
 * that new session must fire the handler again. Platform (non-embed) mode
 * never calls this — its once-per-lifetime semantics are unchanged.
 */
export function resetSessionExpired(): void {
  fired = false;
}
